import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getUsers, createAnyUser, deleteAnyUser } from '../services/api';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';

const ClientUserManagement: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({ username: '', password: '' });
  const isMountedRef = useRef(false);
  const clientIdRef = useRef(user?.clientId);
  clientIdRef.current = user?.clientId;

  const fetchUsers = useCallback(async () => {
    try {
      const fetchedUsers = await getUsers();
      if (!isMountedRef.current) return;
      const cid = clientIdRef.current;
      const clientUsers = fetchedUsers.filter((u: any) =>
        u.client_id === cid && u.role === 'user'
      );
      setUsers(clientUsers);
    } catch {
      if (!isMountedRef.current) return;
      setUsers([]);
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    fetchUsers();
    return () => {
      isMountedRef.current = false;
    };
  }, [fetchUsers, user?.clientId]);

  const handleCreateUser = async () => {
    const userData = {
      username: newUser.username,
      password: newUser.password,
      role: 'user',
      client_id: user?.clientId
    };

    await createAnyUser(userData);
    if (!isMountedRef.current) return;
    setNewUser({ username: '', password: '' });
    fetchUsers();
  };

  const handleDeleteUser = async (userId: string) => {
    if (window.confirm(t('userManagement.confirmDelete'))) {
      await deleteAnyUser(userId, 'user');
      if (!isMountedRef.current) return;
      fetchUsers();
    }
  };

  return (
    <div className="card">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{t('userManagement.title')}</h2>
        <span className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2.5 py-0.5 rounded-full text-sm font-medium">
          {t('userManagement.userCount', { count: users.length })}
        </span>
      </div>

      <form
        className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg mb-6"
        autoComplete="off"
        onSubmit={(e) => e.preventDefault()}
      >
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">{t('userManagement.addNewUser')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('userManagement.username')}
            </label>
            <input
              type="text"
              value={newUser.username}
              onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
              className="input-field"
              placeholder={t('userManagement.enterUsername')}
              autoComplete="off"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('userManagement.password')}
            </label>
            <input
              type="password"
              value={newUser.password}
              onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
              className="input-field"
              placeholder={t('userManagement.enterPassword')}
              autoComplete="new-password"
            />
          </div>
        </div>
        <div className="mt-4">
          <button 
            type="button"
            onClick={handleCreateUser}
            className="btn-primary"
            disabled={!newUser.username || !newUser.password}
          >
            {t('userManagement.addUser')}
          </button>
        </div>
      </form>

      <div className="space-y-3">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">{t('clientProfile.companyUsers')}</h3>
        {users.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <svg className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
            </svg>
            <p className="mt-2">{t('userManagement.noUsers')}</p>
            <p className="text-sm">{t('userManagement.addFirstUser')}</p>
          </div>
        ) : (
          users.map((userItem: any) => (
            <div key={userItem.id} className="flex items-center justify-between p-4 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg hover:border-gray-300 dark:hover:border-gray-500 transition-colors">
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-green-500 dark:bg-green-600 rounded-full flex items-center justify-center">
                    <span className="text-white font-medium text-sm">
                      {userItem.username?.charAt(0)?.toUpperCase() || 'U'}
                    </span>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{userItem.username}</p>
                  <div className="flex items-center space-x-2 mt-1">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                      {t('userManagement.roles.user')}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleDeleteUser(userItem.id)}
                  className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 text-sm font-medium"
                >
                  {t('userManagement.removeUser')}
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ClientUserManagement;
